// @flow
import React, {useState, useEffect, useRef, useCallback} from 'react';

import SortableList from 'react-easy-sort';
import {useHistory} from 'react-router-dom';
import {IconButton, Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {ArrowBack, Add, Category as CategoryIcon} from '@material-ui/icons';
import {useIsFeatureInTier} from '../../../utils/component';

import SnackBar from '../../../elements/SnackBar/SnackBar';
import Skeleton from '@material-ui/lab/Skeleton';

import analytics from '../../../analytics';
import SubcategoryModal from './SubcategoryModal/SubcategoryModal';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import CategoryModal from './CategoryModal/CategoryModal';
import {SIZE} from '../duck';
import {usePage} from '../../page/utils';
import {colors} from '../../../assets/styles/colors';

import type {CategoryRequest} from '../api';
import type {SubcategoryRequest} from '../../sub-categories/duck';
import type {
  Pagination,
  Store,
  User,
  Category,
  Subcategory,
} from '../../../entities';
import queryString from 'query-string';
import ConfirmModal from '../../../elements/Modals/ConfirmModal';
import {useToggle} from '../../../utils/react';
import {
  useInitializeProductCategoryLayout,
  useProductCategoryLayout,
} from '../../home-pages/utils/product-category-layout';
import {arrayMove} from '../../../utils/array';
import CategoryRow from './Category/CategoryRow';
import FeatureAccessBlock from '../../entitlement/components/feature-access-block';

type Props = {
  store?: Store,
  user?: User,
  categories?: Category[],
  pagination?: Pagination,
  isFetching?: boolean,
  isSaving?: boolean,
  resetSubcategoriesState: () => void,
  sortCategories: ({sortMovement?: {[key: string]: any}}) => void,
  sortCategoriesLocal: ({sortRequest?: Object, categories?: string}) => void,
  deleteCategory: ({categoryRequest: CategoryRequest}) => void,
  createCategory: ({categoryRequest: CategoryRequest}) => void,
  editCategory: ({categoryRequest: CategoryRequest}) => void,
  createSubcategory: ({subcategoryRequest: SubcategoryRequest}) => void,
  editSubcategory: ({subcategoryRequest: SubcategoryRequest}) => void,
  deleteSubcategory: ({subcategoryRequest: SubcategoryRequest}) => void,
  sortSubcategory: ({subcategoryRequest: SubcategoryRequest}) => void,
  subcategory?: Subcategory,
  sortSubcategoryError?: string,
  deleteSubcategorySuccess?: boolean,
  deleteSubcategoryError?: string,
  updateSubcategorySuccess?: boolean,
  createCategorySuccess?: boolean,
  fetchCategories: ({reset?: boolean}) => void,
  createCategoryError?: string,
  editCategorySuccess?: boolean,
  editCategoryError?: boolean,
  deleteCategorySuccess?: boolean,
  sortCategoriesError?: boolean,
  sortCategoriesSuccess?: boolean,
  updateSubcategorySuccess?: boolean,
  createSubcategorySuccess?: boolean,
  updateSubcategoryError?: string,
  createSubcategoryError?: string,
  isSavingSubcategory?: boolean,
};

export type Movement = {
  init?: {pos: number, id: string},
  end?: {pos: number, id: string},
};

const OFFSET = 10;
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F9FB',
    maxWidth: 1300,
  },
  content: {
    padding: '.5rem',

    [theme.breakpoints.up('sm')]: {
      padding: '.75rem',
    },

    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  headerWrapper: {
    background: '#f8f9fb',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  subtitle: {
    fontWeight: 'normal',
    color: colors.grey700,
    fontSize: '14px',
    lineHeight: '20px',
    padding: '16px 0',
  },
  card: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '.5rem',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      padding: '.75rem',
    },
  },
  button: {
    border: '1px solid transparent',
    background: '#F5F0FF',
    boxShadow: 'inset 0px 1px 4px rgba(140, 139, 163, 0.12)',
    borderRadius: '12px',
    color: colors.purple,
    width: '100%',
    padding: '5px 10px',
  },
  wrapActionsCategories: {
    width: '100%',
    display: 'flex',
    gap: '.75rem',
    alignItems: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      width: 'fit-content',
    },
  },
  containerDraggable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
  },
}));

function Categories(props: Props) {
  const {
    categories,
    pagination,
    isFetching,
    sortCategories,
    deleteCategory,
    createCategory,
    createCategorySuccess,
    editCategory,
    fetchCategories,
    resetSubcategoriesState,
    editCategorySuccess,
    editCategoryError,
    createCategoryError,
    deleteCategorySuccess,
    sortCategoriesError,
    updateSubcategorySuccess,
    createSubcategorySuccess,
    updateSubcategoryError,
    createSubcategoryError,
    deleteSubcategorySuccess,
    deleteSubcategoryError,
    sortSubcategoryError,
    isSavingSubcategory,
    createSubcategory,
    editSubcategory,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const query = queryString.parse(window.location.search);

  const itemDraggedRef = useRef();

  const [categoriesOrder, setCategoriesOrder] = useState<Category[]>([]);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [itemDragged, setItemDragged] = useState();
  const [movement, setMovement] = useState<Movement | null>(null);
  const [openCategoryModal, setOpenCategoryModal] = useState<boolean>(false);
  const [openSubcategoryModal, setOpenSubcategoryModal] = useState<boolean>(
    false
  );
  const [categorySelected, setCategorySelected] = useState<
    Category | typeof undefined
  >(undefined);

  const [featuredModalOpen, toggleFeaturedModalOpen] = useToggle();

  const [subcategorySelected, setSubcategorySelected] = useState();

  useInitializeProductCategoryLayout();
  const productCategoryLayout = useProductCategoryLayout();

  useEffect(() => {
    if (categories) {
      setCategoriesOrder(categories);
    }
  }, [categories]);

  const handleAddPage = useCallback(() => {
    if (
      !isFetching &&
      categories &&
      pagination &&
      pagination.total > SIZE &&
      categories.length < pagination.total
    ) {
      fetchCategories({reset: false});
    }
  }, [categories, fetchCategories, isFetching, pagination]);

  const handleScroll = useCallback(
    event => {
      if (
        window.innerHeight + window.scrollY + OFFSET >=
        event.target.body.scrollHeight
      ) {
        handleAddPage();
      }
    },
    [handleAddPage]
  );

  useEffect(() => {
    return () => resetSubcategoriesState();
  }, [resetSubcategoriesState]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const onDragEnd = useCallback(() => {
    if (itemDragged && itemDraggedRef.current && movement && categories) {
      setIsDragging(false);
      setItemDragged(null);

      const {init, end} = movement;
      if (init && end && init.pos !== end.pos) {
        sortCategories({
          sortCategoriesRequest: movement,
        });
        setMovement(null);
      }
      //$FlowFixMe
      itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      itemDraggedRef.current = null;
    }
  }, [categories, itemDragged, movement, sortCategories]);

  useEffect(() => {
    if (isDragging && itemDraggedRef && itemDraggedRef.current) {
      itemDraggedRef.current.addEventListener('dragend', onDragEnd);

      return () => {
        itemDraggedRef &&
          itemDraggedRef.current &&
          itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isDragging, onDragEnd]);

  const handleAddCategory = useCallback(() => {
    setCategorySelected(undefined);
    setOpenCategoryModal(true);
  }, [setCategorySelected, setOpenCategoryModal]);

  const handleAddSubcategory = useCallback(() => {
    setCategorySelected(undefined);
    setOpenSubcategoryModal(true);
  }, [setCategorySelected, setOpenSubcategoryModal]);

  const handleEditItem = useCallback(
    (category: Category) => {
      setCategorySelected(category);
      setOpenCategoryModal(true);
    },
    [setCategorySelected, setOpenCategoryModal]
  );

  const handleDeleteItem = useCallback(
    (id: string) => {
      deleteCategory({categoryRequest: {id}});
    },
    [deleteCategory]
  );

  useEffect(() => {
    if (createCategorySuccess) {
      analytics.fireCustomEvent(
        'Category Created',
        undefined,
        ['user-guiding'],
        () => {
          if (query.fromOnboarding) history.push('/home');
        }
      );
    }
  }, [createCategorySuccess, history, query.fromOnboarding]);

  const userHasProductCategoriesFeature = useIsFeatureInTier(
    'PRODUCT_CATEGORIES'
  );

  const handleToggleEnabled = useCallback(
    (id: string, enabled: boolean) => {
      if (!categories) return;
      const category = categories.find(cat => cat.id === id);
      if (!category) return;
      const categoryRequest = {
        id,
        enabled,
        landscapeImageId: category.landscapeImageId,
        portraitImageId: category.portraitImageId,
        originalHorizontalFile: true,
        originalVerticalFile: true,
      };

      editCategory({categoryRequest});
    },
    [categories, editCategory]
  );

  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    const oldId = categoriesOrder[oldIndex].id;
    const newId = categoriesOrder[newIndex].id;

    const sortedSections = arrayMove(categoriesOrder, oldIndex, newIndex);
    const movement = {
      end: {
        id: newId,
        pos: newIndex,
      },
      init: {
        id: oldId,
        pos: oldIndex,
      },
    };

    sortCategories({
      sortCategoriesRequest: movement,
    });
    setCategoriesOrder(sortedSections);
  };

  if (!userHasProductCategoriesFeature) {
    return (
      <div className={classes.root}>
        <div className={classes.content}>
          <div className={classes.headerWrapper}>
            <IconButton
              className={classes.backButton}
              onClick={() => history.goBack()}
            >
              <ArrowBack />
            </IconButton>
            <div>
              <div className={classes.title}>Mis Categorías</div>
            </div>
          </div>
          <div className={classes.subtitle}>
            Las 4 primeras categorías son las que se ven en la portada de tu
            tienda.
          </div>

          <div className={classes.card}>
            <FeatureAccessBlock feature="PRODUCT_CATEGORIES" />
          </div>
        </div>
      </div>
    );
  }

  const categoriesInLayout =
    productCategoryLayout?.productCategoryAisle?.categories;

  return (
    <div>
      <SnackBar
        mode="success"
        message={editCategorySuccess ? 'Categoría actualizada' : ''}
      />
      <SnackBar
        mode="success"
        message={createCategorySuccess ? 'Categoría creada' : ''}
      />
      <SnackBar
        mode="success"
        message={createSubcategorySuccess ? 'Subcategoría creada' : ''}
      />
      <SnackBar
        mode="success"
        message={updateSubcategorySuccess ? 'Subcategoría actualizada' : ''}
      />
      <SnackBar
        mode="success"
        message={deleteSubcategorySuccess ? 'Subcategoría eliminada' : ''}
      />
      <SnackBar
        mode="error"
        message={
          !!createCategoryError
            ? createCategoryError === 'Category already exists'
              ? 'Esta categoría ya fue creada.'
              : 'Error al crear categoría.'
            : ''
        }
      />
      <SnackBar
        mode="error"
        message={editCategoryError ? 'Error al actualizar categoría' : ''}
      />
      <SnackBar
        mode="error"
        message={
          updateSubcategoryError
            ? updateSubcategoryError ===
              'Sub category with requested name already exists'
              ? 'No puedes crear 2 subcategorías con el mismo nombre.'
              : 'Error al actualizar subcategoría'
            : ''
        }
      />
      <SnackBar
        mode="error"
        message={createSubcategoryError ? 'Error al crear subcategoría' : ''}
      />
      <SnackBar
        mode="error"
        message={sortCategoriesError ? 'Error al ordernar categorías' : ''}
      />
      <SnackBar
        mode="success"
        message={deleteCategorySuccess ? 'Categoría eliminada' : ''}
      />
      <SnackBar
        mode="error"
        message={deleteSubcategoryError ? 'Error al eliminar subcategoría' : ''}
      />
      <SnackBar
        mode="error"
        message={sortSubcategoryError ? 'Error al ordenar subcategoría' : ''}
      />

      <div className={classes.root}>
        <div className={classes.content}>
          <div className={classes.headerWrapper}>
            <IconButton
              className={classes.backButton}
              onClick={() => history.goBack()}
            >
              <ArrowBack />
            </IconButton>
            <div>
              <div className={classes.title}>Mis Categorías</div>
            </div>
          </div>
          <div className={classes.subtitle}>
            Las 4 primeras categorías son las que se ven en la portada de tu
            tienda.
          </div>

          <div className={classes.card}>
            <Grid container spacing={1}>
              {userHasProductCategoriesFeature && (
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <div className={classes.wrapActionsCategories}>
                    <CustomButton
                      variant="outlined"
                      className={classes.button}
                      startIcon={<Add />}
                      onClick={handleAddCategory}
                      id="new-category"
                    >
                      Agregar categoría
                    </CustomButton>
                    <CustomButton
                      variant="outlined"
                      className={classes.button}
                      startIcon={<Add />}
                      onClick={handleAddSubcategory}
                      id="new-sub-category"
                    >
                      Agregar subcategoría
                    </CustomButton>
                  </div>
                </Grid>
              )}
              <Grid item xs={12}>
                {categories && (
                  <SortableList
                    className={classes.containerDraggable}
                    draggedItemClassName="dragged"
                    onSortEnd={handleSortEnd}
                  >
                    {categoriesOrder.map((category: Category, idx) => {
                      let featured = idx < 4;
                      if (categoriesInLayout?.length > 0) {
                        featured = categoriesInLayout.includes(category.id);
                      }
                      return (
                        <CategoryRow
                          key={category.id}
                          category={category}
                          handleDeleteItem={handleDeleteItem}
                          handleEditItem={handleEditItem}
                          onToggleFeatured={toggleFeaturedModalOpen}
                          onToggleEnabled={handleToggleEnabled}
                          featured={featured}
                        />
                      );
                    })}
                  </SortableList>
                )}
                {isFetching &&
                  !categories &&
                  Array.from({length: 1}, (x, i) => (
                    <Grid item xs={12} key={i}>
                      <Skeleton variant="text" height="100px" />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={featuredModalOpen}
        message="Sigue la ruta de: Tienda virtual > Páginas > Página de inicio 
        Busca la opción de pasillos de categoría y elige las que desees destacar"
        title="¿Deseas cambiar las categorías destacadas?"
        onCancel={toggleFeaturedModalOpen}
        onConfirm={() => {
          const path = '/admin/my-store/pages/home-page#PRODUCT_CATEGORY_AISLE';
          const origin = window.location.origin;
          const url = `${origin}${path}`;
          window.open(url);
          toggleFeaturedModalOpen();
        }}
        onClose={toggleFeaturedModalOpen}
        hideIcon
        icon={<CategoryIcon fontSize="large" />}
        confirmButtonLabel="Ir"
      />
      {openCategoryModal && (
        <CategoryModal
          open={openCategoryModal}
          handleClose={() => {
            setCategorySelected(undefined);
            setOpenCategoryModal(false);
          }}
          createCategory={createCategory}
          editCategory={editCategory}
          category={categorySelected}
        />
      )}

      <SubcategoryModal
        open={openSubcategoryModal}
        handleClose={() => {
          setSubcategorySelected(undefined);
          setOpenSubcategoryModal(false);
        }}
        categories={categories}
        isSaving={isSavingSubcategory}
        createSubcategory={createSubcategory}
        editSubcategory={editSubcategory}
        createSubcategorySuccess={createSubcategorySuccess}
        updateSubcategorySuccess={updateSubcategorySuccess}
        subcategory={subcategorySelected}
      />
    </div>
  );
}

export default usePage<Props>(Categories, 'Categories');
